import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "./firebase.js";
import { toast } from "react-toastify";

const TABS = [
  { id: "work-orders", label: "Work orders" },
  { id: "amenities", label: "Amenities" },
  { id: "visitors", label: "Visitors" },
  { id: "parking", label: "Parking" },
  { id: "classifieds", label: "Marketplace" },
  { id: "esign", label: "E-sign" },
];

const WORK_STATUS_LABELS = {
  submitted: "Submitted",
  assigned: "Assigned",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

async function fetchUserLotIds(user) {
  if (!user) return [];
  const [s1, s2] = await Promise.all([
    getDocs(query(collection(db, "owners"), where("OwnerId", "==", user.uid))),
    getDocs(query(collection(db, "owners"), where("OwnerId2", "==", user.uid))),
  ]);
  return [...new Set([...s1.docs, ...s2.docs].map((r) => r.id))];
}

function CommunityServicesPage({ user, isAdmin, isMobile }) {
  const [activeTab, setActiveTab] = useState("work-orders");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userLotIds, setUserLotIds] = useState([]);
  const [ownerDirectory, setOwnerDirectory] = useState([]);


  const [workOrders, setWorkOrders] = useState([]);
  const [amenityResources, setAmenityResources] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [permits, setPermits] = useState([]);
  const [classifieds, setClassifieds] = useState([]);
  const [esignDocs, setEsignDocs] = useState([]);
  const [esignSignatures, setEsignSignatures] = useState([]);

  const [workForm, setWorkForm] = useState({ lotId: "", title: "", description: "", category: "general" });
  const [amenityForm, setAmenityForm] = useState({ resourceId: "", lotId: "", startAt: "", endAt: "", notes: "" });
  const [visitorForm, setVisitorForm] = useState({ lotId: "", guestName: "", plate: "", visitStart: "", visitEnd: "", notes: "" });
  const [parkingForm, setParkingForm] = useState({ lotId: "", plate: "", permitType: "guest", validUntil: "", notes: "" });
  const [classifiedForm, setClassifiedForm] = useState({ title: "", description: "", category: "general", price: "" });
  const [esignForm, setEsignForm] = useState({ documentId: "", lotId: "", acknowledgedName: "" });
  const [adminResourceForm, setAdminResourceForm] = useState({ name: "", description: "", maxHours: 4 });
  const [adminEsignForm, setAdminEsignForm] = useState({ title: "", description: "", documentUrl: "", status: "open" });

  const lotLabelById = useMemo(() => {
    const map = new Map();
    ownerDirectory.forEach((lot) => map.set(lot.lotId, lot.tract ? `Lot ${lot.tract}` : lot.lotId));
    return map;
  }, [ownerDirectory]);

  const ownerLotOptions = useMemo(() => {
    if (isAdmin) return ownerDirectory;
    return ownerDirectory.filter((lot) => userLotIds.includes(lot.lotId));
  }, [isAdmin, ownerDirectory, userLotIds]);

  const loadDirectory = useCallback(async () => {
    const snap = await getDocs(collection(db, "owners"));
    const lots = snap.docs.map((row) => ({
      lotId: row.id,
      tract: row.data().tract || row.id,
      address: row.data().address || "",
    }));
    setOwnerDirectory(lots.sort((a, b) => String(a.tract).localeCompare(String(b.tract))));
  }, []);

  const loadTabData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === "work-orders") {
        const res = await httpsCallable(functions, "listWorkOrders")({});
        setWorkOrders(res.data?.orders || []);
      } else if (activeTab === "amenities") {
        const [resRes, resList] = await Promise.all([
          httpsCallable(functions, "listAmenityResources")({}),
          httpsCallable(functions, "listAmenityReservations")({}),
        ]);
        setAmenityResources(resRes.data?.resources || []);
        setReservations(resList.data?.reservations || []);
      } else if (activeTab === "visitors") {
        const res = await httpsCallable(functions, "listVisitorRegistrations")({});
        setVisitors(res.data?.visitors || []);
      } else if (activeTab === "parking") {
        const res = await httpsCallable(functions, "listParkingPermits")({});
        setPermits(res.data?.permits || []);
      } else if (activeTab === "classifieds") {
        const res = await httpsCallable(functions, "listClassifieds")({ includePending: isAdmin });
        setClassifieds(res.data?.listings || []);
      } else if (activeTab === "esign") {
        const res = await httpsCallable(functions, "listEsignDocuments")({});
        setEsignDocs(res.data?.documents || []);
      }
    } catch (err) {
      toast.error(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [activeTab, isAdmin]);

  useEffect(() => {
    (async () => {
      const lots = await fetchUserLotIds(user);
      setUserLotIds(lots);
      await loadDirectory();
    })();
  }, [user, loadDirectory]);

  useEffect(() => {
    loadTabData();
  }, [loadTabData]);

  useEffect(() => {
    if (ownerLotOptions.length === 1) {
      const lotId = ownerLotOptions[0].lotId;
      setWorkForm((f) => (f.lotId ? f : { ...f, lotId }));
      setAmenityForm((f) => (f.lotId ? f : { ...f, lotId }));
      setVisitorForm((f) => (f.lotId ? f : { ...f, lotId }));
      setParkingForm((f) => (f.lotId ? f : { ...f, lotId }));
      setEsignForm((f) => (f.lotId ? f : { ...f, lotId }));
    }
  }, [ownerLotOptions]);

  const handleSubmitWorkOrder = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await httpsCallable(functions, "submitWorkOrder")(workForm);
      toast.success("Work order submitted");
      setWorkForm({ lotId: workForm.lotId, title: "", description: "", category: "general" });
      loadTabData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateWorkOrder = async (orderId, status, extra = {}) => {
    setSaving(true);
    try {
      await httpsCallable(functions, "updateWorkOrderStatus")({ orderId, status, ...extra });
      toast.success("Work order updated");
      loadTabData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBookAmenity = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await httpsCallable(functions, "bookAmenityReservation")(amenityForm);
      toast.success(res.data?.status === "pending" ? "Reservation pending approval" : "Amenity booked");
      setAmenityForm((f) => ({ ...f, startAt: "", endAt: "", notes: "" }));
      loadTabData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddResource = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await httpsCallable(functions, "upsertAmenityResource")(adminResourceForm);
      toast.success("Amenity saved");
      setAdminResourceForm({ name: "", description: "", maxHours: 4 });
      loadTabData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleVisitor = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await httpsCallable(functions, "submitVisitorRegistration")(visitorForm);
      toast.success(res.data?.gateSynced ? "Visitor registered — gate updated" : "Visitor registered");
      setVisitorForm((f) => ({ ...f, guestName: "", plate: "", notes: "" }));
      loadTabData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleParking = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await httpsCallable(functions, "submitParkingPermit")(parkingForm);
      toast.success("Parking permit request submitted");
      setParkingForm((f) => ({ ...f, plate: "", notes: "" }));
      loadTabData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePermitStatus = async (permitId, status) => {
    setSaving(true);
    try {
      await httpsCallable(functions, "updateParkingPermitStatus")({ permitId, status });
      toast.success("Permit updated");
      loadTabData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClassified = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await httpsCallable(functions, "submitClassified")(classifiedForm);
      toast.success("Listing submitted for moderation");
      setClassifiedForm({ title: "", description: "", category: "general", price: "" });
      loadTabData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleModerate = async (listingId, status) => {
    setSaving(true);
    try {
      await httpsCallable(functions, "moderateClassified")({ listingId, status });
      toast.success("Listing updated");
      loadTabData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEsign = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await httpsCallable(functions, "signEsignDocument")(esignForm);
      toast.success("Acknowledgment recorded");
      setEsignForm((f) => ({ ...f, acknowledgedName: "" }));
      loadTabData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateEsign = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await httpsCallable(functions, "upsertEsignDocument")(adminEsignForm);
      toast.success("Document published");
      setAdminEsignForm({ title: "", description: "", documentUrl: "", status: "open" });
      loadTabData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const loadEsignSignatures = async (documentId) => {
    try {
      const res = await httpsCallable(functions, "getEsignSignatureStatus")({ documentId });
      setEsignSignatures(res.data?.signatures || []);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const renderLotSelect = (value, onChange) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} required>
      <option value="">Select lot</option>
      {ownerLotOptions.map((lot) => (
        <option key={lot.lotId} value={lot.lotId}>
          {lot.tract ? `Lot ${lot.tract}` : lot.lotId}
        </option>
      ))}
    </select>
  );

  return (
    <div className={`community-services-page ${isMobile ? "community-services-page-mobile" : ""}`}>
      <header className="community-services-hero">
        <p className="community-services-eyebrow">Owner services</p>
        <h1>Community Services</h1>
        <p>Maintenance requests, amenity booking, visitor registration, parking, marketplace, and e-signatures.</p>
      </header>

      <nav className="community-services-tabs" aria-label="Community services">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={activeTab === tab.id ? "active" : ""}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {loading ? <p className="community-services-loading">Loading…</p> : null}

      {!loading && activeTab === "work-orders" ? (
        <section className="community-services-panel">
          <h2>Maintenance & work orders</h2>
          <form className="community-services-form" onSubmit={handleSubmitWorkOrder}>
            {renderLotSelect(workForm.lotId, (lotId) => setWorkForm((f) => ({ ...f, lotId })))}
            <input placeholder="Title" value={workForm.title} onChange={(e) => setWorkForm((f) => ({ ...f, title: e.target.value }))} required />
            <textarea placeholder="Describe the issue" value={workForm.description} onChange={(e) => setWorkForm((f) => ({ ...f, description: e.target.value }))} rows={3} />
            <button type="submit" className="admin-portal-btn admin-portal-btn-primary" disabled={saving}>Submit request</button>
          </form>
          <div className="community-services-list">
            {workOrders.map((order) => (
              <article key={order.id} className="community-services-card">
                <h3>{order.title}</h3>
                <p>{lotLabelById.get(order.lotId) || order.lotId} · {WORK_STATUS_LABELS[order.status] || order.status}</p>
                <p>{order.description}</p>
                {isAdmin ? (
                  <div className="community-services-actions">
                    <button type="button" className="admin-portal-btn" onClick={() => handleUpdateWorkOrder(order.id, "assigned")}>Assign</button>
                    <button type="button" className="admin-portal-btn" onClick={() => handleUpdateWorkOrder(order.id, "in_progress")}>In progress</button>
                    <button type="button" className="admin-portal-btn admin-portal-btn-primary" onClick={() => handleUpdateWorkOrder(order.id, "completed")}>Complete</button>
                  </div>
                ) : null}
              </article>
            ))}
            {workOrders.length === 0 ? <p>No work orders yet.</p> : null}
          </div>
        </section>
      ) : null}

      {!loading && activeTab === "amenities" ? (
        <section className="community-services-panel">
          <h2>Amenity reservations</h2>
          {isAdmin ? (
            <form className="community-services-form" onSubmit={handleAddResource}>
              <h3>Add amenity (admin)</h3>
              <input placeholder="Name (e.g. Clubhouse)" value={adminResourceForm.name} onChange={(e) => setAdminResourceForm((f) => ({ ...f, name: e.target.value }))} required />
              <input placeholder="Description" value={adminResourceForm.description} onChange={(e) => setAdminResourceForm((f) => ({ ...f, description: e.target.value }))} />
              <button type="submit" className="admin-portal-btn" disabled={saving}>Save amenity</button>
            </form>
          ) : null}
          <form className="community-services-form" onSubmit={handleBookAmenity}>
            <select value={amenityForm.resourceId} onChange={(e) => setAmenityForm((f) => ({ ...f, resourceId: e.target.value }))} required>
              <option value="">Select amenity</option>
              {amenityResources.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            {renderLotSelect(amenityForm.lotId, (lotId) => setAmenityForm((f) => ({ ...f, lotId })))}
            <input type="datetime-local" value={amenityForm.startAt} onChange={(e) => setAmenityForm((f) => ({ ...f, startAt: e.target.value }))} required />
            <input type="datetime-local" value={amenityForm.endAt} onChange={(e) => setAmenityForm((f) => ({ ...f, endAt: e.target.value }))} required />
            <button type="submit" className="admin-portal-btn admin-portal-btn-primary" disabled={saving}>Book</button>
          </form>
          <div className="community-services-list">
            {reservations.map((r) => (
              <article key={r.id} className="community-services-card">
                <h3>{r.resourceName}</h3>
                <p>{lotLabelById.get(r.lotId)} · {r.status}</p>
                <p>{r.startAt ? new Date(r.startAt).toLocaleString() : "—"} – {r.endAt ? new Date(r.endAt).toLocaleString() : "—"}</p>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {!loading && activeTab === "visitors" ? (
        <section className="community-services-panel">
          <h2>Visitor pre-registration</h2>
          <p className="community-services-hint">Registered plates sync to the gate automatically.</p>
          <form className="community-services-form" onSubmit={handleVisitor}>
            {renderLotSelect(visitorForm.lotId, (lotId) => setVisitorForm((f) => ({ ...f, lotId })))}
            <input placeholder="Guest name" value={visitorForm.guestName} onChange={(e) => setVisitorForm((f) => ({ ...f, guestName: e.target.value }))} required />
            <input placeholder="License plate" value={visitorForm.plate} onChange={(e) => setVisitorForm((f) => ({ ...f, plate: e.target.value }))} required />
            <input type="datetime-local" value={visitorForm.visitEnd} onChange={(e) => setVisitorForm((f) => ({ ...f, visitEnd: e.target.value }))} />
            <button type="submit" className="admin-portal-btn admin-portal-btn-primary" disabled={saving}>Register visitor</button>
          </form>
          <div className="community-services-list">
            {visitors.map((v) => (
              <article key={v.id} className="community-services-card">
                <h3>{v.guestName}</h3>
                <p>{v.plate} · {lotLabelById.get(v.lotId)} · {v.status}</p>
                <p>Until {v.visitEnd ? new Date(v.visitEnd).toLocaleString() : "—"}</p>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {!loading && activeTab === "parking" ? (
        <section className="community-services-panel">
          <h2>Parking permits</h2>
          <form className="community-services-form" onSubmit={handleParking}>
            {renderLotSelect(parkingForm.lotId, (lotId) => setParkingForm((f) => ({ ...f, lotId })))}
            <input placeholder="License plate" value={parkingForm.plate} onChange={(e) => setParkingForm((f) => ({ ...f, plate: e.target.value }))} required />
            <select value={parkingForm.permitType} onChange={(e) => setParkingForm((f) => ({ ...f, permitType: e.target.value }))}>
              <option value="guest">Guest</option>
              <option value="annual">Annual</option>
              <option value="temporary">Temporary</option>
            </select>
            <button type="submit" className="admin-portal-btn admin-portal-btn-primary" disabled={saving}>Request permit</button>
          </form>
          <div className="community-services-list">
            {permits.map((p) => (
              <article key={p.id} className="community-services-card">
                <h3>{p.plate}</h3>
                <p>{lotLabelById.get(p.lotId)} · {p.permitType} · {p.status}</p>
                {isAdmin && p.status === "pending" ? (
                  <div className="community-services-actions">
                    <button type="button" className="admin-portal-btn" onClick={() => handlePermitStatus(p.id, "active")}>Approve</button>
                    <button type="button" className="admin-portal-btn" onClick={() => handlePermitStatus(p.id, "revoked")}>Deny</button>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {!loading && activeTab === "classifieds" ? (
        <section className="community-services-panel">
          <h2>Community marketplace</h2>
          <form className="community-services-form" onSubmit={handleClassified}>
            <input placeholder="Title" value={classifiedForm.title} onChange={(e) => setClassifiedForm((f) => ({ ...f, title: e.target.value }))} required />
            <textarea placeholder="Description" value={classifiedForm.description} onChange={(e) => setClassifiedForm((f) => ({ ...f, description: e.target.value }))} rows={3} required />
            <input placeholder="Price (optional)" value={classifiedForm.price} onChange={(e) => setClassifiedForm((f) => ({ ...f, price: e.target.value }))} />
            <button type="submit" className="admin-portal-btn admin-portal-btn-primary" disabled={saving}>Post listing</button>
          </form>
          <div className="community-services-list">
            {classifieds.map((item) => (
              <article key={item.id} className="community-services-card">
                <h3>{item.title}</h3>
                <p>{item.price ? `$${item.price}` : "Make offer"} · {item.status}</p>
                <p>{item.description}</p>
                {isAdmin && item.status === "pending" ? (
                  <div className="community-services-actions">
                    <button type="button" className="admin-portal-btn admin-portal-btn-primary" onClick={() => handleModerate(item.id, "published")}>Publish</button>
                    <button type="button" className="admin-portal-btn" onClick={() => handleModerate(item.id, "rejected")}>Reject</button>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {!loading && activeTab === "esign" ? (
        <section className="community-services-panel">
          <h2>Document acknowledgments</h2>
          {isAdmin ? (
            <form className="community-services-form" onSubmit={handleCreateEsign}>
              <h3>Publish document (admin)</h3>
              <input placeholder="Title" value={adminEsignForm.title} onChange={(e) => setAdminEsignForm((f) => ({ ...f, title: e.target.value }))} required />
              <input placeholder="Document URL" value={adminEsignForm.documentUrl} onChange={(e) => setAdminEsignForm((f) => ({ ...f, documentUrl: e.target.value }))} />
              <textarea placeholder="Description" value={adminEsignForm.description} onChange={(e) => setAdminEsignForm((f) => ({ ...f, description: e.target.value }))} rows={2} />
              <button type="submit" className="admin-portal-btn" disabled={saving}>Open for signatures</button>
            </form>
          ) : null}
          {esignDocs.filter((d) => d.status === "open").map((doc) => (
            <div key={doc.id} className="community-services-card">
              <h3>{doc.title}</h3>
              <p>{doc.description}</p>
              {doc.documentUrl ? <a href={doc.documentUrl} target="_blank" rel="noopener noreferrer">View document</a> : null}
              <form className="community-services-form" onSubmit={handleEsign}>
                <input type="hidden" value={doc.id} onChange={() => {}} />
                {renderLotSelect(esignForm.lotId, (lotId) => setEsignForm((f) => ({ ...f, lotId, documentId: doc.id })))}
                <input placeholder="Type your full name to acknowledge" value={esignForm.documentId === doc.id ? esignForm.acknowledgedName : ""} onChange={(e) => setEsignForm({ documentId: doc.id, lotId: esignForm.lotId, acknowledgedName: e.target.value })} required />
                <button type="submit" className="admin-portal-btn admin-portal-btn-primary" disabled={saving}>Sign / acknowledge</button>
              </form>
              {isAdmin ? (
                <button type="button" className="admin-portal-btn" onClick={() => loadEsignSignatures(doc.id)}>View signatures ({doc.signatureCount || 0})</button>
              ) : null}
            </div>
          ))}
          {esignSignatures.length > 0 ? (
            <ul className="community-services-signatures">
              {esignSignatures.map((s) => (
                <li key={s.id}>{s.acknowledgedName} — Lot {s.lotId} — {s.signedAt ? new Date(s.signedAt).toLocaleString() : ""}</li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

export default CommunityServicesPage;